import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Shield } from "lucide-react";

const inp = "w-full bg-white/5 border border-white/12 rounded-lg px-4 py-3 outline-none focus:border-[#1F5EFF] text-white text-[15px]";

/**
 * Public-facing estimate approval page. No auth required — secure token in URL.
 * Route: /estimate/:token
 */
export default function PublicEstimate() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${BACKEND}/api/crm/public/estimates/${token}`)
      .then((r) => r.ok ? r.json() : r.text().then((t) => { throw new Error(t); }))
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, [token, BACKEND]);

  const respond = async (action) => {
    if (!name.trim()) { toast.error("Enter your name to sign"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/crm/public/estimates/${token}/respond`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, customer_name: name, note }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success(action === "approve" ? "Estimate approved" : "Estimate declined");
      const d = await r.json();
      setData({ ...data, estimate: { ...data.estimate, status: d.status } });
    } catch (e) { toast.error(String(e)); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-[#08080b] text-white flex items-center justify-center p-10 text-red-300">{err}</div>;
  if (!data) return <div className="min-h-screen bg-[#08080b] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const e = data.estimate;
  const decided = e.status === "approved" || e.status === "declined";

  return (
    <div className="min-h-screen bg-[#08080b] text-white" data-testid="public-estimate">
      <div className="max-w-3xl mx-auto p-6 md:p-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#1F5EFF] flex items-center justify-center font-black">W</div>
          <div>
            <div className="font-display text-xl">WTD Detailing &amp; Tint</div>
            <div className="font-mono-alt text-[10px] text-white/40 uppercase tracking-widest">Estimate {e.est_number}</div>
          </div>
        </div>

        <div className="p-6 md:p-8 rounded-2xl bg-white/5 border border-white/10 mb-6">
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div><div className="text-[10px] font-mono-alt text-white/40 uppercase mb-1">Prepared for</div><div>{data.customer?.first_name} {data.customer?.last_name}</div></div>
            <div><div className="text-[10px] font-mono-alt text-white/40 uppercase mb-1">Vehicle</div><div>{[data.vehicle?.year, data.vehicle?.make, data.vehicle?.model].filter(Boolean).join(" ")}</div></div>
            <div><div className="text-[10px] font-mono-alt text-white/40 uppercase mb-1">Issued</div><div>{new Date(e.created_at).toLocaleDateString("en-CA")}</div></div>
            <div><div className="text-[10px] font-mono-alt text-white/40 uppercase mb-1">Expires</div><div>{e.expires_at ? new Date(e.expires_at).toLocaleDateString("en-CA") : "—"}</div></div>
          </div>
          <div className="space-y-2 mb-6">
            {e.items.map((it, i) => {
              const line = it.qty * it.unit_price - (it.discount || 0);
              return (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/3 border border-white/8">
                  <div className="flex-1">
                    <div className="text-white">{it.name}</div>
                    {it.description && <div className="text-white/40 text-xs">{it.description}</div>}
                  </div>
                  <div className="text-white/50 text-xs">
                    {it.qty} × ${it.unit_price}
                    {Number(it.discount) > 0 && (
                      <span className="text-emerald-300/80"> −${Number(it.discount).toFixed(2)}{it.discount_pct ? ` (${it.discount_pct}%)` : ""}</span>
                    )}
                  </div>
                  <div className="font-display text-[#8db2ff] tabular-nums w-20 text-right">${line.toFixed(2)}</div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-white/10 pt-4 space-y-1 text-sm">
            <div className="flex justify-between text-white/60"><span>Subtotal</span><span>${e.subtotal?.toFixed(2)}</span></div>
            {e.discount_total > 0 && <div className="flex justify-between text-white/60"><span>Discount</span><span>−${e.discount_total?.toFixed(2)}</span></div>}
            <div className="flex justify-between text-white/60"><span>GST ({e.tax_rate}%)</span><span>${e.tax_total?.toFixed(2)}</span></div>
            <div className="flex justify-between font-display text-2xl pt-2 mt-2 border-t border-white/10 text-white"><span>Total</span><span className="text-[#8db2ff]">${e.total?.toFixed(2)}</span></div>
          </div>
        </div>

        {decided ? (
          <div className={`p-6 rounded-xl ${e.status === "approved" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-red-500/10 border border-red-500/30 text-red-300"} flex items-center gap-3`}>
            {e.status === "approved" ? <CheckCircle2 className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
            <div>
              <div className="font-display text-lg capitalize">{e.status}</div>
              <div className="text-sm opacity-80">Thank you — the studio has been notified.</div>
            </div>
          </div>
        ) : (
          <div className="p-6 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-[#1F5EFF]" />
              <span className="font-mono-alt text-[10px] text-[#8db2ff] uppercase tracking-widest">Respond to this estimate</span>
            </div>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name (as signature)" className={inp + " mb-3"} data-testid="public-est-name" />
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note to the studio…" className={inp + " mb-4"} data-testid="public-est-note" />
            <div className="flex gap-2">
              <button onClick={() => respond("approve")} disabled={busy} data-testid="public-est-approve"
                className="flex-1 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-40">
                Approve estimate
              </button>
              <button onClick={() => respond("decline")} disabled={busy} data-testid="public-est-decline"
                className="px-6 py-3 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white font-medium">
                Decline
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
